<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4 border">
      <CCardBody class="p-4">
        <div class="d-flex flex-wrap justify-content-between align-items-center">
          <div class="mb-2 mb-md-0">
            <h1 class="h4 mb-1 font-weight-bold">Bank accounts</h1>
            <p class="text-muted mb-0">
              Your business bank and cash accounts, with their statements
            </p>
          </div>
          <div class="d-flex align-items-center">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              title="Refresh"
              :disabled="loading"
              @click="refreshTable"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add bank account
            </CButton>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- Summary (only once there is something to summarise) -->
    <CRow v-if="items.length" class="mb-2">
      <CCol sm="6" lg="3" class="mb-3">
        <CCard class="h-100 border mb-0">
          <CCardBody class="p-3">
            <div class="text-muted small mb-1">Accounts</div>
            <div class="font-weight-bold h4 mb-0">{{ bankSummary.totalAccounts }}</div>
            <div class="small text-muted">{{ bankSummary.activeAccounts }} active</div>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol sm="6" lg="3" class="mb-3">
        <CCard class="h-100 border mb-0">
          <CCardBody class="p-3">
            <div class="text-muted small mb-1">Total balance</div>
            <div class="font-weight-bold h4 mb-0">{{ formatCurrency(bankSummary.totalBalance) }}</div>
            <div class="small text-muted">Current balance, accounts shown</div>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol sm="6" lg="3" class="mb-3">
        <CCard class="h-100 border mb-0">
          <CCardBody class="p-3">
            <div class="text-muted small mb-1">Statements</div>
            <div class="font-weight-bold h4 mb-0">{{ bankSummary.totalStatements }}</div>
            <div class="small text-muted">Uploaded, all time</div>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol sm="6" lg="3" class="mb-3">
        <CCard class="h-100 border mb-0">
          <CCardBody class="p-3">
            <div class="text-muted small mb-1">Low balance</div>
            <div class="font-weight-bold h4 mb-0">{{ bankSummary.lowBalanceAccounts }}</div>
            <div class="small text-muted">Below their alert amount, today</div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Bank Accounts List -->
    <CCard class="mb-4 border">
      <CCardBody class="p-4">
        <!-- Filters -->
        <CRow>
          <CCol sm="6" lg="3">
            <CInput
              label="Bank"
              :value="filters.bankName"
              placeholder="Search bank name"
              @input="onTextFilter('bankName', $event)"
            />
          </CCol>
          <CCol sm="6" lg="3">
            <CInput
              label="Account number"
              :value="filters.accountNumber"
              placeholder="Search account number"
              inputmode="numeric"
              @input="onTextFilter('accountNumber', $event)"
            />
          </CCol>
          <CCol sm="6" lg="3">
            <CSelect
              label="Account type"
              :value="filters.accountType"
              :options="accountTypeFilterOptions"
              @update:value="onSelectFilter('accountType', $event)"
            />
          </CCol>
          <CCol sm="6" lg="3">
            <CSelect
              label="Status"
              :value="filters.status"
              :options="statusFilterOptions"
              @update:value="onSelectFilter('status', $event)"
            />
          </CCol>
        </CRow>
        <div v-if="hasActiveFilters" class="d-flex align-items-center mb-3">
          <span class="small text-muted mr-2">
            {{ activeFiltersCount }} filter{{ activeFiltersCount === 1 ? '' : 's' }} on
          </span>
          <CButton color="link" size="sm" class="p-0" @click="resetFilters">Clear filters</CButton>
        </div>

        <!-- Refresh with data on screen: keep the data, show a thin bar -->
        <CProgress v-if="loading && items.length" :value="100" animated size="xs" class="mb-3" />

        <!-- First load -->
        <div v-if="loading && !items.length" class="text-center py-5">
          <CSpinner color="primary" />
          <p class="mt-3 mb-0 text-muted">Loading bank accounts…</p>
        </div>

        <!-- Error -->
        <div v-else-if="loadError && !items.length" class="text-center py-5">
          <CIcon name="cil-warning" size="2xl" class="text-danger mb-3" />
          <h5>Couldn't load your bank accounts</h5>
          <p class="text-muted mb-3">Check your connection and try again.</p>
          <CButton color="primary" @click="refreshTable">
            <CIcon name="cil-reload" /> Try again
          </CButton>
        </div>

        <!-- Empty (first use) -->
        <div v-else-if="!items.length && !hasActiveFilters" class="text-center py-5">
          <CIcon name="cil-bank" size="2xl" class="text-muted mb-3" />
          <h5>No bank accounts yet</h5>
          <p class="text-muted mb-3">
            Add the accounts your business uses so you can track balances and upload statements.
          </p>
          <CButton color="primary" @click="addNew">
            <CIcon name="cil-plus" /> Add bank account
          </CButton>
        </div>

        <!-- Empty (filtered) -->
        <div v-else-if="!items.length" class="text-center py-4">
          <CIcon name="cil-filter-x" size="xl" class="text-muted mb-2" />
          <h6>No bank accounts match your filters</h6>
          <CButton color="light" class="mt-2" @click="resetFilters">
            <CIcon name="cil-x" /> Clear filters
          </CButton>
        </div>

        <template v-else>
          <!-- Desktop View -->
          <div class="d-none d-lg-block">
            <CDataTable
              :items="computedItems"
              :fields="fields"
              :pagination="{
                align: 'end',
                doubleArrows: true
              }"
              :sorter="{ external: true }"
              :table-filter="false"
              :items-per-page-select="{
                values: [10, 25, 50],
                label: 'Rows per page:'
              }"
              :items-per-page="perPage"
              :active-page="currentPage"
              :pages="totalPages"
              hover
              responsive
              @pagination-change="onPaginationChange"
              @items-per-page-change="onItemsPerPageChange"
              @sorter-value-change="onSorterChange"
            >
              <!-- Bank Logo & Name Column -->
              <template #bankInfo="{ item }">
                <td class="py-2">
                  <div class="d-flex align-items-center">
                    <div class="c-avatar c-avatar-md bg-light mr-3 d-flex align-items-center justify-content-center">
                      <img v-if="getBankLogo(item.name)" :src="getBankLogo(item.name)" :alt="item.name" class="img-fluid p-1" style="max-height: 32px;" />
                      <CIcon v-else name="cil-bank" class="text-muted" />
                    </div>
                    <div>
                      <router-link :to="`/tenants/Bank/${item.id}`" class="font-weight-bold">
                        {{ item.name }}
                      </router-link>
                      <small class="d-block text-muted">{{ item.accountType || 'Business Account' }}</small>
                    </div>
                  </div>
                </td>
              </template>

              <!-- Account Details Column -->
              <template #accountDetails="{ item }">
                <td class="py-2">
                  <div>{{ item.accountName }}</div>
                  <small class="text-muted text-monospace">{{ formatAccountNumber(item.accountNo) }}</small>
                </td>
              </template>

              <!-- Balance Column -->
              <template #balance="{ item }">
                <td class="py-2 text-right">
                  <div class="font-weight-bold" :class="{ 'text-danger': Number(item.balance) < 0 }">
                    {{ formatCurrency(item.balance || 0) }}
                  </div>
                  <CBadge v-if="isLowBalance(item)" color="warning">Low balance</CBadge>
                </td>
              </template>

              <!-- Statements Count -->
              <template #statementsCount="{ item }">
                <td class="py-2 text-center">
                  {{ statementsCount(item) }}
                </td>
              </template>

              <!-- Status Column -->
              <template #status="{ item }">
                <td class="py-2">
                  <CBadge :color="getStatusColor(item.status || 'Active')">
                    {{ item.status || 'Active' }}
                  </CBadge>
                </td>
              </template>

              <!-- Last Updated -->
              <template #lastUpdated="{ item }">
                <td class="py-2">
                  {{ formatDate(item.updatedAt || item.createdAt) }}
                </td>
              </template>

              <!-- Actions Column -->
              <template #show_details="{ item }">
                <td class="py-2">
                  <div class="text-right action-buttons">
                    <CButtonGroup>
                      <CButton
                        color="light"
                        class="border"
                        title="Edit"
                        aria-label="Edit bank account"
                        @click="onEdit(item)"
                      >
                        <CIcon name="cil-pencil" />
                      </CButton>
                      <CDropdown
                        color="light"
                        placement="bottom-end"
                        class="dropdown-with-zindex border"
                        aria-label="More actions"
                      >
                        <template #toggler-content>
                          <CIcon name="cil-options" />
                        </template>
                        <CDropdownItem @click="viewStatements(item)">
                          <CIcon name="cil-file" class="mr-2" /> View statements
                        </CDropdownItem>
                        <CDropdownItem @click="uploadStatement(item)">
                          <CIcon name="cil-cloud-upload" class="mr-2" /> Upload statement
                        </CDropdownItem>
                        <CDropdownDivider />
                        <CDropdownItem class="text-danger" @click="showDeleteConfirmation(item)">
                          <CIcon name="cil-trash" class="mr-2" /> Delete
                        </CDropdownItem>
                      </CDropdown>
                    </CButtonGroup>
                  </div>
                </td>
              </template>
            </CDataTable>
          </div>

          <!-- Mobile View -->
          <div class="d-lg-none">
            <div
              v-for="item in computedItems"
              :key="item.id"
              class="border rounded p-3 mb-2"
            >
              <div class="d-flex justify-content-between align-items-start">
                <div class="mr-2">
                  <router-link :to="`/tenants/Bank/${item.id}`" class="font-weight-bold">
                    {{ item.accountName || item.name }}
                  </router-link>
                  <div class="small text-muted">{{ item.name }}</div>
                  <div class="small text-muted text-monospace">{{ formatAccountNumber(item.accountNo) }}</div>
                </div>
                <CBadge :color="getStatusColor(item.status || 'Active')">
                  {{ item.status || 'Active' }}
                </CBadge>
              </div>
              <div class="d-flex justify-content-between align-items-center mt-2">
                <div>
                  <div class="font-weight-bold" :class="{ 'text-danger': Number(item.balance) < 0 }">
                    {{ formatCurrency(item.balance || 0) }}
                  </div>
                  <CBadge v-if="isLowBalance(item)" color="warning">Low balance</CBadge>
                </div>
                <CDropdown color="light" placement="bottom-end" class="dropdown-mobile border">
                  <template #toggler-content>
                    <CIcon name="cil-options" /> More
                  </template>
                  <CDropdownItem @click="onEdit(item)">
                    <CIcon name="cil-pencil" class="mr-2" /> Edit
                  </CDropdownItem>
                  <CDropdownItem @click="uploadStatement(item)">
                    <CIcon name="cil-cloud-upload" class="mr-2" /> Upload statement
                  </CDropdownItem>
                  <CDropdownDivider />
                  <CDropdownItem class="text-danger" @click="showDeleteConfirmation(item)">
                    <CIcon name="cil-trash" class="mr-2" /> Delete
                  </CDropdownItem>
                </CDropdown>
              </div>
            </div>

            <!-- Mobile Pagination -->
            <div v-if="totalPages > 1" class="mt-3 d-flex justify-content-center">
              <CPagination
                :activePage="currentPage"
                :pages="totalPages"
                @update:activePage="onPageChange"
              />
            </div>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <!-- Delete Confirmation Modal -->
    <CModal
      :title="`Delete ${itemToDelete.accountName || itemToDelete.name || 'this bank account'}?`"
      :show.sync="warningModal"
      @update:show="onDeleteConfirmation"
    >
      <p class="mb-2">
        The {{ itemToDelete.name }} account
        <span v-if="itemToDelete.accountNo" class="text-monospace">{{ itemToDelete.accountNo }}</span>
        and its {{ statementsCount(itemToDelete) }} uploaded
        statement{{ statementsCount(itemToDelete) === 1 ? '' : 's' }} will be removed.
        This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">
          Keep it
        </CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          {{ deleting ? "Deleting…" : "Delete account" }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import BankApi from "@/lib/bankApi";
import { formatDate } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";

export default {
  name: "BankList",
  data() {
    return {
      loading: true,
      loadError: false,
      deleting: false,
      items: [],
      fields: [
        { key: "bankInfo", label: "Bank", sorter: true, filter: true },
        { key: "accountDetails", label: "Account", sorter: true, filter: true },
        {
          key: "balance",
          label: "Balance",
          sorter: true,
          filter: false,
          _style: "text-align: right; width: 140px;",
        },
        {
          key: "statementsCount",
          label: "Statements",
          sorter: false,
          filter: false,
          _style: "text-align: center; width: 100px;",
        },
        { key: "status", label: "Status", sorter: true, filter: true, _style: "width: 80px;" },
        { key: "lastUpdated", label: "Last updated", sorter: true, filter: false },
        { key: "show_details", label: "", _style: "width:120px", sorter: false, filter: false },
      ],
      api: new BankApi(),
      warningModal: false,
      itemToDelete: {},
      // Pagination
      currentPage: 1,
      perPage: 25,
      totalItems: 0,
      totalPages: 1,
      // Sorting
      sortBy: "name",
      sortDirection: "asc",
      // Filters
      filters: {
        bankName: "",
        accountNumber: "",
        accountType: "",
        status: "",
      },
      filterTimer: null,
      // Values match the options on the bank account form (Bank.vue).
      accountTypeFilterOptions: [
        { value: "", label: "All types" },
        { value: "Current Account", label: "Current account" },
        { value: "Savings Account", label: "Savings account" },
        { value: "Business Account", label: "Business account" },
        { value: "Fixed Deposit", label: "Fixed deposit" },
        { value: "Credit Account", label: "Credit account" },
        { value: "Investment Account", label: "Investment account" },
        { value: "Cash Account", label: "Cash account" },
      ],
      statusFilterOptions: [
        { value: "", label: "All statuses" },
        { value: "Active", label: "Active" },
        { value: "Inactive", label: "Inactive" },
        { value: "Closed", label: "Closed" },
      ],
      // Summary data
      bankSummary: {
        totalAccounts: 0,
        activeAccounts: 0,
        totalBalance: 0,
        totalStatements: 0,
        lowBalanceAccounts: 0,
      },
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Accounting", "Bank accounts"]);
    this.refreshTable();
  },
  beforeDestroy() {
    clearTimeout(this.filterTimer);
  },
  computed: {
    computedItems() {
      return this.items;
    },
    hasActiveFilters() {
      return Object.values(this.filters).some((filter) => filter !== "");
    },
    activeFiltersCount() {
      return Object.values(this.filters).filter((filter) => filter !== "").length;
    },
  },
  methods: {
    formatCurrency(amount) {
      const value = Number(amount) || 0;
      return value < 0 ? `-RM ${formatMyr(-value)}` : `RM ${formatMyr(value)}`;
    },
    formatDate(dateString) {
      return formatDate(dateString) || "—";
    },
    formatAccountNumber(accountNumber) {
      if (!accountNumber) return "—";
      const parts = accountNumber.split("-");
      if (parts.length === 3) {
        return `•••• •••• ${parts[2]}`;
      }
      return accountNumber;
    },
    statementsCount(item) {
      return (item && item.bankStatements && item.bankStatements.length) || 0;
    },
    isLowBalance(item) {
      const min = Number(item.minBalance) || 0;
      return min > 0 && (Number(item.balance) || 0) < min;
    },
    getBankLogo(bankName) {
      const bankLogos = {
        'Malayan Banking Berhad': 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a0/Maybank_Logo.svg/1200px-Maybank_Logo.svg.png',
        'CIMB Bank Berhad': 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0c/CIMB_Group_logo.svg/1200px-CIMB_Group_logo.svg.png',
        'Public Bank Berhad': 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/1a/Public_Bank_logo.svg/1200px-Public_Bank_logo.svg.png',
        'Hong Leong Bank Berhad': 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/3a/Hong_Leong_Bank_logo.svg/1200px-Hong_Leong_Bank_logo.svg.png',
        'RHB Bank Berhad': 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f0/RHB_Bank_logo.svg/1200px-RHB_Bank_logo.svg.png'
      };
      return bankLogos[bankName] || null;
    },
    // UI-GUIDELINES 3.3 `bankAccount`; Suspended needs someone to act, so warning.
    getStatusColor(status) {
      const statusColors = {
        Active: "success",
        Inactive: "secondary",
        Closed: "secondary",
        Suspended: "warning",
      };
      return statusColors[status] || "secondary";
    },
    refreshTable() {
      this.loading = true;
      this.loadError = false;

      const params = {
        page: this.currentPage,
        pageSize: this.perPage,
        sortBy: this.sortBy,
        sortDirection: this.sortDirection,
      };

      // Add filters
      if (this.filters.bankName) {
        params.bankName = this.filters.bankName;
      }
      if (this.filters.accountNumber) {
        params.accountNumber = this.filters.accountNumber;
      }
      if (this.filters.accountType) {
        params.accountType = this.filters.accountType;
      }
      if (this.filters.status) {
        params.status = this.filters.status;
      }

      this.api
        .getListByCurrentBusiness(params)
        .then((response) => {
          if (response && response.data) {
            this.items = response.data || [];
            this.totalItems = response.totalRecords || 0;
            this.currentPage = response.page || 1;
            this.totalPages = response.totalPages || 1;
          } else {
            this.items = (response && response.result) || response || [];
            this.totalItems = this.items.length;
            this.totalPages = Math.ceil(this.totalItems / this.perPage);
          }

          if (this.totalPages < 1) {
            this.totalPages = 1;
          }

          this.loadSummaryData();
        })
        .catch((error) => {
          console.error("Bank accounts load failed", error);
          this.loadError = true;
          if (this.items.length) {
            this.$toast.error("Couldn't refresh your bank accounts. Check your connection and try again.");
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadSummaryData() {
      let totalBalance = 0;
      let totalStatements = 0;
      let activeAccounts = 0;
      let lowBalanceAccounts = 0;

      this.items.forEach((item) => {
        totalBalance += Number(item.balance) || 0;
        totalStatements += this.statementsCount(item);
        if (item.status === "Active" || !item.status) activeAccounts++;
        if (this.isLowBalance(item)) lowBalanceAccounts++;
      });

      this.bankSummary = {
        totalAccounts: this.totalItems,
        activeAccounts,
        totalBalance,
        totalStatements,
        lowBalanceAccounts,
      };
    },
    onEdit(item) {
      this.$router.push({ path: `/tenants/Bank/${item.id}` });
    },
    viewStatements(item) {
      this.$router.push({ path: `/tenants/Bank/${item.id}#statements` });
    },
    uploadStatement(item) {
      this.$router.push({ path: `/tenants/Bank/${item.id}?action=upload` });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    confirmDelete() {
      if (this.deleting) return;
      this.deleting = true;
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.$toast.success("Bank account deleted");
          this.warningModal = false;
          this.refreshTable();
        })
        .catch((error) => {
          console.error("Bank account delete failed", error);
          this.$toast.error(
            "Couldn't delete the bank account. " +
              helper.getErrorMessage(error && error.data, "Check your connection and try again.")
          );
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    onDeleteConfirmation(status) {
      if (!status && !this.deleting) {
        this.itemToDelete = {};
      }
    },
    addNew() {
      this.$router.push({ path: "/tenants/Bank" });
    },
    // Pagination methods
    onPaginationChange(paginationData) {
      if (paginationData !== this.perPage) {
        this.perPage = parseInt(paginationData);
        this.currentPage = 1;
        this.refreshTable();
      }
    },
    onPageChange(page) {
      if (page !== this.currentPage) {
        this.currentPage = page;
        this.refreshTable();
      }
    },
    onItemsPerPageChange(itemsPerPage) {
      if (itemsPerPage !== this.perPage) {
        this.perPage = parseInt(itemsPerPage);
        this.currentPage = 1;
        this.refreshTable();
      }
    },
    onSorterChange(sorter) {
      if (sorter && sorter.column) {
        this.sortBy = sorter.column;
        this.sortDirection = sorter.asc ? "asc" : "desc";
        this.currentPage = 1;
        this.refreshTable();
      }
    },
    // Filter methods: text filters are debounced, selects apply at once.
    onTextFilter(key, value) {
      this.filters[key] = value;
      clearTimeout(this.filterTimer);
      this.filterTimer = setTimeout(this.applyFilters, 300);
    },
    onSelectFilter(key, value) {
      this.filters[key] = value;
      this.applyFilters();
    },
    resetFilters() {
      this.filters = {
        bankName: "",
        accountNumber: "",
        accountType: "",
        status: "",
      };
      this.applyFilters();
    },
    applyFilters() {
      this.currentPage = 1;
      this.refreshTable();
    },
  },
};
</script>

<style scoped>
/* Fix dropdown z-index issues */
.dropdown-with-zindex {
  position: static;
}

/* Lift the open row above its neighbours so the dropdown menu always
   renders on top instead of behind the rows below it. */
.action-buttons,
.action-buttons .btn-group {
  position: relative;
  z-index: auto;
}

.action-buttons:has(.dropdown.show),
.action-buttons .btn-group:has(.dropdown.show) {
  z-index: 1060;
}
</style>
